"use client";

import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from "react";
import type { RefObject } from "react";
import { usePlaceSearch } from "@ishvaram/ui/hooks";
import type { PlaceResult } from "@ishvaram/ui/hooks";
import { searchPlaces } from "@/lib/api/misc";
import { isPlaceResult } from "@/lib/place-result";
import { formatPlaceOptionLabel } from "./PlaceAutocomplete.option";

interface PlaceCityData {
  name: string;
  state: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

interface PlaceAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (place: PlaceResult) => void;
  id?: string;
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
}

interface PlaceAutocompleteDropdownProps {
  id: string;
  listRef: RefObject<HTMLUListElement | null>;
  results: PlaceResult[];
  activeIndex: number;
  onSelect: (place: PlaceResult | undefined) => void;
  setActiveIndex: (index: number) => void;
}

// 3,776 Indian cities (population ≥ 15k) live in /public/data/place-cities.json (~400KB).
// Loaded on first focus instead of bundled — saves ~400KB from initial JS payload
// for every visitor who doesn't open the autocomplete.
let _citiesPromise: Promise<PlaceResult[]> | null = null;
function loadLocalCities(): Promise<PlaceResult[]> {
  if (_citiesPromise) return _citiesPromise;
  _citiesPromise = fetch("/data/place-cities.json")
    .then((r) => (r.ok ? r.json() : []))
    .then((rows: PlaceCityData[]) =>
      rows.map((c, i) => ({
        geoname_id: -(i + 1), // negative = local, never clash with GeoNames
        name: c.name,
        display: `${c.name}, ${c.state}, India`,
        latitude: c.latitude,
        longitude: c.longitude,
        timezone: c.timezone,
        country: "IN",
        population: 0,
      }))
    )
    .catch(() => []);
  return _citiesPromise;
}

function filterLocal(cities: PlaceResult[], query: string): PlaceResult[] {
  if (!query.trim()) return cities.slice(0, 8);
  const q = query.toLowerCase();
  // Prefix matches first (array is population-sorted), then substring matches
  const prefix: PlaceResult[] = [];
  const substr: PlaceResult[] = [];
  for (const c of cities) {
    if (c.name.toLowerCase().startsWith(q)) {
      prefix.push(c);
      if (prefix.length === 8) break;
    } else if (c.display.toLowerCase().includes(q)) {
      substr.push(c);
    }
  }
  return [...prefix, ...substr].slice(0, 8);
}

function normalizeDisplay(value: string) {
  return value
    .toLowerCase()
    .replace(/,\s*india$/, "")
    .trim();
}

function mergePlaceResults(
  value: string,
  localMatches: PlaceResult[],
  safeApiResults: PlaceResult[]
) {
  if (value.trim().length < 4) return localMatches;
  const apiExtra = safeApiResults.filter((apiPlace) => {
    const apiKey = normalizeDisplay(apiPlace.display);
    return !localMatches.some((localPlace) => {
      if (normalizeDisplay(localPlace.display) === apiKey) return true;
      return (
        localPlace.name.toLowerCase() === apiPlace.name.toLowerCase() &&
        Math.abs(localPlace.latitude - apiPlace.latitude) < 0.01 &&
        Math.abs(localPlace.longitude - apiPlace.longitude) < 0.01
      );
    });
  });
  return [...localMatches, ...apiExtra];
}

export default function PlaceAutocomplete({
  value,
  onChange,
  onSelect,
  id = "birthPlace",
  placeholder = "Start typing a city (e.g. Mumbai, Delhi, Karnal)",
  required = true,
  invalid = false,
  describedBy,
  className = "input-sacred w-full rounded-lg px-4 py-3 pr-9 text-[14px]",
}: PlaceAutocompleteProps) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [localCities, setLocalCities] = useState<PlaceResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setIsHydrated(true), 0);
    return () => window.clearTimeout(id);
  }, []);

  // Lazy-load the 3,776-city local index on first focus (cached after first hit).
  useEffect(() => {
    if (!focused || localCities.length > 0) return;
    let cancelled = false;
    loadLocalCities().then((rows) => {
      if (!cancelled) setLocalCities(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [focused, localCities.length]);

  // Local matches for the current query — used as fallback below minChars
  // and as the prefix layer in merged results above minChars.
  const localMatches = useMemo(() => filterLocal(localCities, value), [localCities, value]);

  // Skip the /_places API entirely when the local 3,776-city index already
  // has enough matches — most users type a major city the local index fully
  // covers, so the API call (for rare small towns) is pure waste. Only when
  // local has < LOCAL_SUFFICIENT matches do we wake the Worker. This is the
  // main lever cutting the ~2.2M/mo /_places Worker invocations; local results
  // still render instantly, so there is no perceptible UX change.
  const LOCAL_SUFFICIENT = 5;
  const shouldFetch = useCallback(
    () => localMatches.length < LOCAL_SUFFICIENT,
    [localMatches.length]
  );

  // Pass localMatches as fallbackResults so the hook returns them when
  // query.length < minChars (empty field → top-8 cities, 1-3 chars → filtered).
  // At/above minChars the hook fetches from the API and returns apiResults.
  // minChars=4 + debounceMs=350 keeps short queries on the local 3,776-city
  // index (which covers every Indian city ≥15k pop) and only hits the API
  // when the user is searching for a small town the local index doesn't have.
  // The hook also caches each query's results in-memory, so backspacing or
  // re-typing a prefix never re-fires the network call.
  const { apiResults, isFetching } = usePlaceSearch({
    query: value,
    searchFn: searchPlaces,
    debounceMs: 350,
    minChars: 4,
    fallbackResults: localMatches,
    shouldFetch,
  });
  const safeApiResults = apiResults.filter(isPlaceResult);

  // Four branches:
  //   empty field   → top-8 local cities
  //   1-3 chars     → filtered local cities (no API hit — local index covers
  //                   every Indian city ≥15k pop)
  //   ≥4 chars      → local matches + API extras (deduped) for rare-town reach,
  //                   and only when local matches are insufficient (shouldFetch)
  const results: PlaceResult[] = mergePlaceResults(value, localMatches, safeApiResults);

  const isOpen = focused && results.length > 0;
  // Show spinner when API is in flight and no local matches surfaced yet
  const isSearching =
    focused && value.trim().length >= 4 && isFetching && localMatches.length === 0;
  const activeOptionId = activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined;

  useEffect(() => {
    startTransition(() => setActiveIndex(-1));
  }, [results.length]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    listRef.current
      .querySelector<HTMLLIElement>(`[data-option-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(place: PlaceResult | undefined) {
    if (!isPlaceResult(place)) return;
    onChange(place.display);
    onSelect(place);
    setFocused(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        id={id}
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        placeholder={placeholder}
        className={className}
        style={{ color: "var(--ish-dark)" }}
        disabled={!isHydrated}
        autoComplete="off"
        autoCapitalize="words"
        spellCheck={false}
        role="combobox"
        data-hydrated={isHydrated ? "true" : "false"}
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        aria-required={required || undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />

      {isSearching && <SearchSpinner />}
      {isOpen && (
        <PlaceAutocompleteDropdown
          id={id}
          listRef={listRef}
          results={results}
          activeIndex={activeIndex}
          onSelect={handleSelect}
          setActiveIndex={setActiveIndex}
        />
      )}
    </div>
  );
}

function SearchSpinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: "50%",
        right: 12,
        transform: "translateY(-50%)",
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: "2px solid rgba(0,0,0,0.12)",
        borderTopColor: "var(--ish-accent, #b9644a)",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}

function PlaceAutocompleteDropdown({
  id,
  listRef,
  results,
  activeIndex,
  onSelect,
  setActiveIndex,
}: PlaceAutocompleteDropdownProps) {
  return (
    <ul
      ref={listRef}
      id={`${id}-listbox`}
      role="listbox"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 50,
        maxHeight: "240px",
        overflowY: "auto",
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "0 0 8px 8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        margin: 0,
        padding: 0,
        listStyle: "none",
      }}
    >
      {results.map((place, index) => (
        <PlaceOption
          key={place.geoname_id}
          id={`${id}-option-${index}`}
          place={place}
          index={index}
          isActive={index === activeIndex}
          isLast={index === results.length - 1}
          onSelect={onSelect}
          setActiveIndex={setActiveIndex}
        />
      ))}
    </ul>
  );
}

function PlaceOption({
  id,
  place,
  index,
  isActive,
  isLast,
  onSelect,
  setActiveIndex,
}: {
  id: string;
  place: PlaceResult;
  index: number;
  isActive: boolean;
  isLast: boolean;
  onSelect: (place: PlaceResult | undefined) => void;
  setActiveIndex: (index: number) => void;
}) {
  const isLocal = place.geoname_id < 0;
  const { cityName, region, label } = formatPlaceOptionLabel(place.display);
  return (
    <li
      id={id}
      data-option-index={index}
      role="option"
      aria-selected={isActive}
      aria-label={label}
      onMouseDown={() => onSelect(place)}
      onMouseEnter={() => setActiveIndex(index)}
      style={{
        padding: "10px 14px",
        cursor: "pointer",
        fontSize: "14px",
        color: "var(--ish-dark, #1a1a1a)",
        backgroundColor: isActive ? "var(--ish-accent-light)" : "transparent",
        borderBottom: isLast ? "none" : "1px solid var(--ish-border-light)",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {isLocal && (
        <span style={{ fontSize: 12, color: "var(--ish-accent)", flexShrink: 0 }}>★</span>
      )}
      <span>
        <span style={{ fontWeight: 500 }}>{cityName}</span>
        {region && (
          <span style={{ color: "var(--ish-text-faint, #888)", marginLeft: 6, fontSize: 12 }}>
            {`, ${region}`}
          </span>
        )}
      </span>
    </li>
  );
}
